import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { FeaturedItem, TeamStanding, getImageUrl } from '@/services/api'
import { StandingsSkeleton } from './Skeleton'
import { useTeamDisplay } from '@/utils/teamDisplay'
import { useSportForSeason } from '@/contexts/SportContext'
import BracketWidget from './BracketWidget'
import { sf } from '../../../core/features/sf'
import { useLabels } from '../../../core/labels'
import { formatPct } from '../../../core/pages/home/derive'
import { siteCoreMessages } from '@/i18n/catalogs/site-core'
import { useT } from '@/i18n'
import { siteClassicMessages } from '@/i18n/catalogs/site-classic'

interface SwipeableStandingsProps {
  featured: FeaturedItem[]
  loading?: boolean
}

// Inner table — extracted so the sport hook can resolve per featured
// item without violating the rules of hooks. The basketball standings
// pack mirrors the legacy GP/W/L/PF/PA/+/- columns 1:1, so basketball
// tenants see no visible change. Exported for the stacked division
// standings (#583), which pass a `caption` and `fullHeight` (no inner
// scroll); without them the DOM is today's.
export function StandingsTable({
  featured,
  isBracketTournament,
  onTeamClick,
  teamName,
  caption,
  fullHeight = false,
}: {
  featured: FeaturedItem
  isBracketTournament: boolean
  onTeamClick: (teamId: number) => void
  teamName: (team: TeamStanding, mode: 'auto' | 'short' | 'full') => string
  caption?: string
  fullHeight?: boolean
}) {
  const t = useLabels()
  const m = useT(siteClassicMessages).schedule.standings
  // Season featured items expose contentId === season_id. Tournament
  // items don't carry season_id directly here, so we fall back to the
  // league default (basketball today) — bracket-tournament columns are
  // sport-agnostic anyway.
  const seasonId = featured.type === 'season' ? featured.contentId : null
  const sport = useSportForSeason(seasonId)

  // Bracket tournaments keep the existing sport-agnostic column set —
  // seed/W/L/Status reads identically for every sport.
  // #69: when the backend resolves a configurable ranking strategy, its
  // column descriptors travel inside featured.rankingStrategy.columns. We
  // prefer that over the sport-pack default so a season can opt into
  // FIBA-style columns (PTS, class_pts) without changing its sport.
  const renderColumns = isBracketTournament
    ? null
    : featured.rankingStrategy?.columns ?? sport.standings.columns

  // Helper to read any field off a TeamStanding row by column key.
  // Soccer rows include extras (draws, goals_for, goals_against, pts)
  // that aren't on the TeamStanding type; cast to a loose record.
  const getCell = (team: TeamStanding, key: string): number => {
    const v = (team as unknown as Record<string, unknown>)[key]
    return typeof v === 'number' ? v : 0
  }

  return (
    // Opaque ground, the same colour as the sticky # / Team cells, so the
    // sticky columns don't paint a darker band down the table (#789).
    <div className="bg-bg-primary border border-secondary/30">
      {/* #630: a scroll box keyboards can reach (it holds no focusable content of its own). */}
      <div className={fullHeight ? 'overflow-x-auto scrollbar-hide' : 'max-h-[480px] overflow-auto scrollbar-hide'} tabIndex={0} role="region" aria-label={caption ?? t.term('standings')}>
        <table className="w-full border-collapse">
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead className="bg-bg-primary/20 border-b border-secondary/30 sticky top-0 z-20">
            <tr>
              <th className="px-2 sm:px-3 py-3 text-left text-xs uppercase tracking-[0.22em] font-bold text-white/70 bg-bg-primary sticky left-0 z-30">#</th>
              <th className={`px-2 sm:px-3 py-3 text-left text-xs uppercase tracking-[0.22em] font-bold text-white/70 bg-bg-primary sticky left-[40px] z-30 min-w-[100px]${fullHeight ? ' lg:w-[38%]' : ''}`}>{m.team}</th>
              {isBracketTournament ? (
                <>
                  <th className="px-3 py-3 text-center text-xs uppercase tracking-[0.22em] font-bold text-white/70 bg-bg-primary/20 whitespace-nowrap">{m.seed}</th>
                  <th className="px-3 py-3 text-center text-xs uppercase tracking-[0.22em] font-bold text-white/70 bg-bg-primary/20 whitespace-nowrap">{m.wins}</th>
                  <th className="px-3 py-3 text-center text-xs uppercase tracking-[0.22em] font-bold text-white/70 bg-bg-primary/20 whitespace-nowrap">{m.losses}</th>
                  <th className="px-3 py-3 text-center text-xs uppercase tracking-[0.22em] font-bold text-white/70 bg-bg-primary/20 whitespace-nowrap">{m.status}</th>
                </>
              ) : (
                renderColumns!.map((col) => (
                  <th
                    key={col.key}
                    title={col.description}
                    className="px-3 py-3 text-center text-xs uppercase tracking-[0.22em] font-bold text-white/70 bg-bg-primary/20 whitespace-nowrap"
                  >
                    {col.label}
                  </th>
                ))
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {featured.standings.map((team: TeamStanding) => (
              <tr
                key={team.id}
                onClick={() => onTeamClick(team.id)}
                className="cursor-pointer hover:bg-white/5 transition-colors"
              >
                <td className="px-2 sm:px-3 py-3 sticky left-0 z-10 bg-bg-primary">
                  <span
                    className={`w-6 h-6 inline-flex items-center justify-center text-xs font-['Montserrat'] font-black ${
                      team.rank === 1 ? 'bg-primary text-black' : team.rank <= 3 ? 'text-primary' : 'text-white/55'
                    }`}
                  >
                    {team.rank}
                  </span>
                </td>
                <td className="px-2 sm:px-3 py-3 sticky left-[40px] z-10 bg-bg-primary">
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    {team.logo_url && (
                      <img
                        src={getImageUrl(team.logo_url) || ''}
                        alt={team.name}
                        className="w-6 h-6 object-contain rounded flex-shrink-0"
                      />
                    )}
                    <span className="text-white/90 font-bold text-sm">
                      {teamName(team, 'auto')}
                    </span>
                  </div>
                </td>
                {isBracketTournament ? (
                  <>
                    <td className="px-3 py-3 text-center text-white/80 text-sm">{team.seed ?? '—'}</td>
                    <td className="px-3 py-3 text-center text-white/80 text-sm font-bold">{team.wins}</td>
                    <td className="px-3 py-3 text-center text-white/80 text-sm font-bold">{team.losses}</td>
                    <td className="px-3 py-3 text-right">
                      <span className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider inline-block ${
                        team.status === 'champion'
                          ? 'bg-primary/20 border border-primary/30 text-primary'
                          : team.status === 'alive'
                          ? 'bg-green-500/20 border border-green-500/30 text-green-400'
                          : 'bg-red-500/20 border border-red-500/30 text-red-400'
                      }`}>
                        {team.status === 'champion' ? '🏆 ' + t.bracketStatus('champion') : t.bracketStatus(team.status)}
                      </span>
                    </td>
                  </>
                ) : (
                  renderColumns!.map((col) => {
                    // Goal/point diff cells keep their colored-diff treatment.
                    const isDiff = col.key === 'point_diff' || col.key === 'goal_diff'
                    const value = getCell(team, col.key)
                    if (isDiff) {
                      return (
                        <td
                          key={col.key}
                          className={`px-3 py-3 text-center text-sm font-['Montserrat'] font-black ${
                            value > 0 ? 'text-green-400' : value < 0 ? 'text-red-400' : 'text-white/75'
                          }`}
                        >
                          {value > 0 ? '+' : ''}{value || 0}
                        </td>
                      )
                    }
                    // Bold the win/loss/draw/points columns to match the
                    // legacy basketball treatment (W and L were bold).
                    // #69: class_pts is included for forthcoming FIBA-style
                    // strategies (#70) so the primary points column reads
                    // bold the way the soccer Pts column does today.
                    const isOutcome =
                      col.key === 'wins' ||
                      col.key === 'losses' ||
                      col.key === 'draws' ||
                      col.key === 'pts' ||
                      col.key === 'class_pts' ||
                      col.primary
                    const cls = isOutcome
                      ? 'px-3 py-3 text-center text-white/80 text-sm font-bold'
                      : 'px-3 py-3 text-center text-white/80 text-sm'
                    return (
                      <td key={col.key} className={cls}>
                        {/* The win % strategy's PCT column prints as .750 (#651). */}
                        {col.key === 'winPct' ? formatPct(value) : value || 0}
                      </td>
                    )
                  })
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default function SwipeableStandings({ 
  featured, 
  loading = false 
}: SwipeableStandingsProps) {
  const navigate = useNavigate()
  const teamName = useTeamDisplay()
  const t = useLabels()
  const m = useT(siteClassicMessages).schedule.standings
  const noData = useT(siteCoreMessages).standings.noData
  const [currentIndex, setCurrentIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchEnd, setTouchEnd] = useState<number | null>(null)

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
  }

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return
    
    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance

    if (isLeftSwipe && currentIndex < featured.length - 1) {
      setCurrentIndex(prev => prev + 1)
    }
    if (isRightSwipe && currentIndex > 0) {
      setCurrentIndex(prev => prev - 1)
    }
  }

  // Reset index if featured items change
  useEffect(() => {
    if (currentIndex >= featured.length) {
      setCurrentIndex(Math.max(0, featured.length - 1))
    }
  }, [featured.length, currentIndex])

  if (loading) {
    return (
      <div className="bg-bg-secondary/35 backdrop-blur-md border border-secondary/20 px-4 py-4">
        <div className="flex items-baseline justify-between mb-3">
          <div className="h-5 w-32 bg-white/10 animate-pulse rounded" />
          <div className="h-3 w-10 bg-white/10 animate-pulse rounded" />
        </div>
        <div className="bg-bg-primary/12 border border-secondary/30">
          <StandingsSkeleton />
        </div>
      </div>
    )
  }

  if (featured.length === 0) {
    return (
      <div className="bg-bg-secondary/35 backdrop-blur-md border border-secondary/20 px-4 py-4">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="text-sm sm:text-base font-['Montserrat'] font-black text-white uppercase tracking-tight">
            {t.term('standings')}
          </h3>
        </div>
        <div className="bg-bg-primary/12 border border-secondary/30 p-4 text-center text-white/60 text-sm">
          {m.none}
        </div>
      </div>
    )
  }

  // Guard against a stale currentIndex when `featured` shrinks below it.
  // The clamping useEffect runs *after* render, so reading featured[currentIndex]
  // directly would crash on the render right after the shrink. Clamp inline.
  const safeIndex = Math.min(currentIndex, featured.length - 1)
  const currentFeatured = featured[safeIndex]

  // Check if this is a bracket tournament (has status field in standings)
  const isBracketTournament = currentFeatured.standings.length > 0 &&
    currentFeatured.standings.some(team => team.status !== undefined)

  return (
    <div className="bg-bg-secondary/35 backdrop-blur-md border border-secondary/20 px-4 py-4" {...sf('home.section.standings')}>
      {/* Header with title and dots */}
      <div className="flex items-baseline justify-between mb-3">
        <h3 
          id="standings" 
          className="text-base sm:text-lg font-['Montserrat'] font-black text-white uppercase tracking-tight"
        >
          {m.heading(currentFeatured.name)}
        </h3>
        {featured.length > 1 && (
          <div className="flex items-center gap-2">
            <span className="text-white/55 text-[10px] uppercase tracking-[0.22em] font-bold">
              {safeIndex + 1} / {featured.length}
            </span>
          </div>
        )}
      </div>

      {/* Swipeable Container */}
      <div 
        ref={containerRef}
        className="relative overflow-hidden"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* For single-elimination tournaments, swap the standings table
            for the actual bracket tree — same view fans see on the
            /schedule page, just tighter styling. Falls back to the
            standings table if the widget can't render (endpoint fail,
            no games, etc.). */}
        {currentFeatured.type === 'tournament' && isBracketTournament ? (
          <BracketWidget
            tournamentId={currentFeatured.contentId}
            fallback={
              <StandingsTable
                featured={currentFeatured}
                isBracketTournament={isBracketTournament}
                onTeamClick={(teamId) => navigate(`/teams/${teamId}`)}
                teamName={teamName}
              />
            }
          />
        ) : currentFeatured.standings.length > 0 ? (
          <StandingsTable
            featured={currentFeatured}
            isBracketTournament={isBracketTournament}
            onTeamClick={(teamId) => navigate(`/teams/${teamId}`)}
            teamName={teamName}
          />
        ) : (
          <div className="bg-bg-primary/12 border border-secondary/30 p-4 text-center text-white/60 text-sm">
            {noData}
          </div>
        )}
      </div>

      {/* Dot Indicators (only show if multiple featured items) */}
      {featured.length > 1 && (
        // #630: each dot is a 24px target; the drawn dot stays 8px.
        <div className="flex items-center justify-center mt-3">
          {featured.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className="group grid place-items-center w-6 h-6"
              aria-label={m.goTo(featured[index].name)}
            >
              <span
                className={`block w-2 h-2 rounded-full transition-all ${
                  index === safeIndex
                    ? 'bg-primary w-4'
                    : 'bg-white/30 group-hover:bg-white/50'
                }`}
              />
            </button>
          ))}
        </div>
      )}

      {/* Swipe hint (only on mobile with multiple items) */}
      {featured.length > 1 && (
        <div className="lg:hidden text-center mt-1">
          <span className="text-white/30 text-[10px] uppercase tracking-wider">
            {m.swipe}
          </span>
        </div>
      )}

      {/* Navigation arrows for desktop */}
      {featured.length > 1 && (
        <div className="hidden lg:flex items-center justify-center gap-4 mt-3">
          <button
            onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
            disabled={safeIndex === 0}
            className={`p-1 rounded transition-all ${
              safeIndex === 0
                ? 'text-white/20 cursor-not-allowed'
                : 'text-white/60 hover:text-white'
            }`}
            aria-label={m.previous}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-white/60 text-xs">
            {safeIndex + 1} / {featured.length}
          </span>
          <button
            onClick={() => setCurrentIndex(prev => Math.min(featured.length - 1, prev + 1))}
            disabled={safeIndex === featured.length - 1}
            className={`p-1 rounded transition-all ${
              safeIndex === featured.length - 1
                ? 'text-white/20 cursor-not-allowed'
                : 'text-white/60 hover:text-white'
            }`}
            aria-label={m.next}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}
    </div>
  )
}

